import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useMixpanel } from '../hooks/useMixpanel';
import { useMixpanelUser } from '../hooks/useMixpanelUser';

interface MixpanelContextType {
  track: (event: string, properties?: Record<string, any>) => void;
  identify: (userId: string, properties?: Record<string, any>) => void;
  trackAction: (action: string, properties?: Record<string, any>) => void;
  trackPage: (pageName: string, properties?: Record<string, any>) => void;
  trackError: (error: Error, context?: Record<string, any>) => void;
  setUserProperties: (properties: Record<string, any>) => void;
}

const MixpanelContext = createContext<MixpanelContextType | undefined>(undefined);

interface MixpanelProviderProps {
  children: ReactNode;
  userId?: string;
  userProperties?: Record<string, any>;
  trackPageViews?: boolean;
  trackErrors?: boolean;
}

export const MixpanelProvider: React.FC<MixpanelProviderProps> = ({
  children,
  userId,
  userProperties,
  trackPageViews = true,
  trackErrors = true
}) => {
  const { userEmail } = useMixpanelUser();
  const mixpanel = useMixpanel({ trackPageViews, trackErrors, userEmail });

  useEffect(() => {
    if (userId) {
      mixpanel.identify(userId, userProperties);
    }
  }, [userId, userProperties, mixpanel]);

  return (
    <MixpanelContext.Provider value={mixpanel}>
      {children}
    </MixpanelContext.Provider>
  );
};

export const useMixpanelContext = (): MixpanelContextType => {
  const context = useContext(MixpanelContext);
  if (!context) {
    throw new Error('useMixpanelContext must be used within a MixpanelProvider');
  }
  return context;
};
